import React, { useState } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';

const Section = styled.section`
  padding: 100px 24px;
  background-color: #f8fafc;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h2`
  font-size: 48px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 20px;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 60px;
`;

const ToggleLabel = styled.span<{ active: boolean }>`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.active ? '#0f172a' : '#94a3b8'};
  transition: color 0.3s ease;
`;

const ToggleSwitch = styled.button<{ active: boolean }>`
  width: 56px;
  height: 32px;
  background: ${props => props.active ? '#002147' : '#cbd5e1'};
  border-radius: 100px;
  border: none;
  position: relative;
  cursor: pointer;
  transition: background 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    top: 4px;
    left: ${props => props.active ? '28px' : '4px'};
    width: 24px;
    height: 24px;
    background: white;
    border-radius: 50%;
    transition: left 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
`;

const SaveBadge = styled.span`
  background: #dcfce7;
  color: #166534;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 100px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  align-items: stretch; // Ensure all cards stretch to the same height

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div) <{ popular?: boolean }>`
  background: white;
  border-radius: 24px;
  padding: 40px;
  position: relative;
  border: 2px solid ${props => props.popular ? '#002147' : 'transparent'};
  box-shadow: ${props => props.popular ? '0 25px 50px -12px rgba(0, 33, 71, 0.15)' : '0 10px 30px -10px rgba(0,0,0,0.05)'};
  z-index: ${props => props.popular ? 2 : 1};
  display: flex;
  flex-direction: column;
  height: 100%; // Take full height of the grid cell

  ${props => props.popular && `
    &::before {
      content: 'MOST POPULAR';
      position: absolute;
      top: -16px;
      left: 50%;
      transform: translateX(-50%);
      background: #002147;
      color: white;
      padding: 8px 16px;
      border-radius: 100px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.05em;
    }
  `}
`;

const PlanName = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 12px;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 24px;
`;

const Currency = styled.span`
  font-size: 24px;
  font-weight: 600;
  color: #64748b;
`;

const Amount = styled.span`
  font-size: 48px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const Period = styled.span`
  font-size: 16px;
  color: #64748b;
  font-weight: 500;
`;

const Description = styled.p`
  font-size: 16px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 32px;
  min-height: 48px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 40px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-grow: 1; // Push the button to the bottom
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 16px;
  color: #334155;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    color: #002147;
    margin-top: 2px;
  }
`;

const Button = styled(motion.button) <{ variant?: 'primary' | 'outline' }>`
  width: 100%;
  padding: 18px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: auto; // Ensure it stays at the bottom

  ${props => props.variant === 'primary' ? `
    background: #002147;
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(0, 33, 71, 0.2);

    &:hover {
      background: #003366;
      transform: translateY(-2px);
    }
  ` : `
    background: transparent;
    color: #002147;
    border: 2px solid #e2e8f0;

    &:hover {
      border-color: #002147;
      background: #f8fafc;
    }
  `}
`;

const EducationPricing: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(true);

  const plans = [
    {
      name: "Starter",
      price: isAnnual ? "19" : "24",
      description: "For small departments or individual tutors starting with automation.",
      features: [
        "1 Admissions Assistant",
        "1 Enrollment Form",
        "1,000 Student Queries/mo",
        "20 Document Uploads",
        "Train on 30 Course Pages",
        "Basic Student Insights",
        "3 Month History"
      ],
      cta: "Get Started",
      variant: "outline"
    },
    {
      name: "Pro",
      price: isAnnual ? "30" : "38",
      popular: true,
      description: "For growing institutions needing more capacity and integrations.",
      features: [
        "3 Admissions Assistants",
        "10 Enrollment Forms",
        "50,000 Student Queries/mo",
        "100 Document Uploads",
        "Train on 1,000 Course Pages",
        "Advanced Student Insights",
        "1 Year History",
        "Custom Branding"
      ],
      cta: "Get Started",
      variant: "primary"
    },
    {
      name: "Unlimited",
      price: isAnnual ? "100" : "125",
      description: "For universities and large organizations requiring full scale.",
      features: [
        "Unlimited Assistants",
        "Unlimited Enrollment Forms",
        "500,000 Student Queries/mo",
        "500 Document Uploads",
        "Train on 5,000 Course Pages",
        "Full Campus Analytics",
        "3 Year History",
        "Live Advisor Handoff",
        "Unlimited Student Workflows"
      ],
      cta: "Contact Sales",
      variant: "outline"
    }
  ];

  return (
    <Section id="pricing">
      <Container>
        <Header>
          <Title>Simple, Transparent Pricing</Title>
          <Subtitle>
            Choose the plan that fits your institution's size. No hidden setup fees.
          </Subtitle>
        </Header>

        <ToggleContainer>
          <ToggleLabel active={!isAnnual}>Monthly</ToggleLabel>
          <ToggleSwitch active={isAnnual} onClick={() => setIsAnnual(!isAnnual)} />
          <ToggleLabel active={isAnnual}>Yearly</ToggleLabel>
          <SaveBadge>SAVE 20%</SaveBadge>
        </ToggleContainer>

        <Grid>
          {plans.map((plan, index) => (
            <Card
              key={plan.name}
              popular={plan.popular}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <PlanName>{plan.name}</PlanName>
              <PriceContainer>
                <Currency>$</Currency>
                <Amount>{plan.price}</Amount>
                <Period>/mo</Period>
              </PriceContainer>
              <Description>{plan.description}</Description>

              <FeatureList>
                {plan.features.map((feature) => (
                  <FeatureItem key={feature}>
                    <Check size={20} />
                    {feature}
                  </FeatureItem>
                ))}
              </FeatureList>

              <Button
                variant={plan.variant as any}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {plan.cta} <ArrowRight size={18} />
              </Button>
            </Card>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default EducationPricing;
